/**
 * Agency portal — Client Scorecard (SYR-85).
 *
 * The in-portal drill-down for one client: BE hero with arc gauge, benchmark
 * position, building-block tiles, rolling-bucket trend (Overview conventions:
 * n≥10 hides a bucket, amber ring marks low-confidence buckets), strengths /
 * focus areas, and recent activity. Token-swap to the full dashboard stays as
 * a secondary action.
 */

import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Spin, Tooltip } from 'antd';
import {
    ArrowLeftOutlined,
    ExportOutlined,
    LockOutlined,
} from '@ant-design/icons';
import {
    CartesianGrid,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip as ChartTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import { useAgencyContext } from '../../hooks/useAgencyContext';
import { useImpersonation } from '../../hooks/useImpersonation';
import {
    useGetAgencyBrandTrendQuery,
    useGetAgencyClientScorecardQuery,
} from '../../services/nestjs-base-api';
import { THEME } from '../../components/MarketerView/config/theme';
import {
    METER_GRADIENT,
    bandFromClassification,
    formatBipolarScore,
    gaugePosition,
} from '../../components/MarketerView/config/bandColours';
import { colourForScore } from '../../components/MarketerView/config/elementColours';
import { BandTag, Card, EquityArcGauge } from '../../components/MarketerView/atoms';
import { useAgencyScope } from '../shell/AgencyScopeContext';
import {
    AgencyEyebrow,
    BucketBadges,
    DeltaChip,
    SignalChip,
    benchmarkPositionLabel,
    secondaryBtnStyle,
} from '../components/agencyUi';

const LINE_COLOUR = '#3d5a80';
const LOW_CONFIDENCE_COLOUR = '#d97706';

function formatEventType(eventType: string): string {
    return eventType
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

function timeAgo(iso: string): string {
    const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
}

export const AgencyClientScorecardPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const { clientOrgId } = useParams<{ clientOrgId: string }>();
    const { user } = useAuth();
    const { period } = useAgencyScope();
    const { switchToClient } = useAgencyContext();
    const { isImpersonating } = useImpersonation();
    const [brandId, setBrandId] = useState<number | undefined>(undefined);

    const orgId = parseInt(clientOrgId || '0', 10);

    const { data, isLoading, error } = useGetAgencyClientScorecardQuery(
        { jwtToken: user?.jwtToken || '', clientOrgId: orgId, period, brandId },
        { skip: !orgId },
    );

    const { data: trend } = useGetAgencyBrandTrendQuery(
        {
            jwtToken: user?.jwtToken || '',
            brandId: data?.selectedBrandId || 0,
            period,
        },
        { skip: !data?.selectedBrandId },
    );

    const trendPoints = useMemo(() => {
        if (!trend) return [];
        return trend.timeSeriesData.map((p) => ({
            month: p.month,
            score:
                p.totalResponses >= trend.minResponseThreshold
                    ? Math.round(p.overallBrandEquityScore * 10) / 10
                    : null,
            responses: p.totalResponses,
            lowConfidence:
                p.totalResponses >= trend.minResponseThreshold &&
                p.totalResponses < trend.lowConfidenceThreshold,
        }));
    }, [trend]);

    const categories = useMemo(() => {
        const unlocked = (data?.equity.questionCategoryScores || []).filter(
            (c) => !c.locked && c.meanScore !== null,
        );
        // Rank by classification band (scale-aware — Price Deals & co. have
        // their own scales), tie-broken by raw score.
        const BAND_RANK: Record<string, number> = {
            'Unbelievably Strong': 5,
            Excellent: 4,
            Good: 3,
            Neutral: 2,
            'Needs Attention': 1,
            'Priority Concern': 0,
        };
        const rank = (c: (typeof unlocked)[number]): number =>
            BAND_RANK[c.classificationLabel ?? ''] ?? 2;
        const sorted = [...unlocked].sort(
            (a, b) => rank(b) - rank(a) || (b.meanScore ?? 0) - (a.meanScore ?? 0),
        );
        return {
            strengths: sorted.slice(0, 3),
            focus: sorted.slice(-3).reverse(),
        };
    }, [data]);

    if (isLoading || !data) {
        return (
            <div style={{ padding: 96, textAlign: 'center' }}>
                {error ? (
                    <Alert
                        type="error"
                        showIcon
                        message={intl.formatMessage({
                            id: 'agency.scorecard.error',
                            defaultMessage: 'Could not load this client',
                        })}
                    />
                ) : (
                    <Spin size="large" />
                )}
            </div>
        );
    }

    const { client, equity, benchmark, bucket, signals } = data;
    const positionLabel = benchmarkPositionLabel(benchmark?.quartile);
    const score = equity.score;

    return (
        <div>
            {/* Back + header */}
            <Link
                to="/agency"
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    color: THEME.textSecondary,
                    fontSize: 13.5,
                    fontWeight: 600,
                    marginBottom: 16,
                }}
            >
                <ArrowLeftOutlined />
                <FormattedMessage id="agency.scorecard.back" defaultMessage="Back to portfolio" />
            </Link>

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 16,
                    flexWrap: 'wrap',
                    marginBottom: 24,
                }}
            >
                <div style={{ minWidth: 0 }}>
                    <h1
                        style={{
                            fontSize: 30,
                            fontWeight: 700,
                            letterSpacing: '-0.02em',
                            margin: 0,
                            lineHeight: 1.12,
                        }}
                    >
                        {client.clientOrganisationName}
                    </h1>
                    <div
                        style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            marginTop: 10,
                        }}
                    >
                        <BucketBadges bucket={bucket} />
                        {client.billingResponsibility === 'AGENCY' && (
                            <span
                                style={{
                                    fontSize: 11.5,
                                    fontWeight: 700,
                                    color: THEME.primaryDark,
                                    background: THEME.primarySoft,
                                    padding: '2px 9px',
                                    borderRadius: 999,
                                }}
                            >
                                <FormattedMessage
                                    id="agency.scorecard.agencyBilled"
                                    defaultMessage="Agency-billed"
                                />
                            </span>
                        )}
                        {/* insufficient_data omitted — the equity hero already
                            shows the collecting-data state. */}
                        {signals
                            .filter((s) => s.type !== 'insufficient_data')
                            .map((s) => (
                                <SignalChip key={s.type} signal={s} />
                            ))}
                    </div>
                    {client.notes && (
                        <p
                            style={{
                                margin: '10px 0 0',
                                fontSize: 13.5,
                                fontStyle: 'italic',
                                color: THEME.textMuted,
                                maxWidth: 560,
                            }}
                        >
                            {client.notes}
                        </p>
                    )}
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Brand switcher — hidden for single-brand clients */}
                    {data.brands.length > 1 &&
                        data.brands.map((b) => (
                            <button
                                key={b.brandId}
                                type="button"
                                onClick={() => setBrandId(b.brandId)}
                                style={{
                                    ...secondaryBtnStyle,
                                    padding: '7px 14px',
                                    borderRadius: 999,
                                    fontSize: 13,
                                    ...(b.brandId === data.selectedBrandId
                                        ? {
                                              background: THEME.primarySoft,
                                              color: THEME.primaryDark,
                                              border: `1px solid ${THEME.primary}`,
                                          }
                                        : {}),
                                }}
                            >
                                {b.brandName}
                            </button>
                        ))}
                    <Tooltip
                        title={
                            isImpersonating
                                ? intl.formatMessage({
                                      id: 'agency.portfolio.openDisabled',
                                      defaultMessage: 'Unavailable while impersonating',
                                  })
                                : undefined
                        }
                    >
                        <button
                            type="button"
                            disabled={isImpersonating}
                            style={{
                                ...secondaryBtnStyle,
                                opacity: isImpersonating ? 0.5 : 1,
                                cursor: isImpersonating ? 'not-allowed' : 'pointer',
                            }}
                            onClick={() => switchToClient(client.clientOrganisationId)}
                        >
                            <ExportOutlined />
                            <FormattedMessage
                                id="agency.scorecard.openDashboard"
                                defaultMessage="Open full dashboard"
                            />
                        </button>
                    </Tooltip>
                </div>
            </div>

            {/* Hero: gauge + benchmark */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(300px, 5fr) minmax(300px, 7fr)',
                    gap: 16,
                    marginBottom: 28,
                }}
            >
                <Card>
                    <AgencyEyebrow>
                        <FormattedMessage
                            id="agency.scorecard.equityTitle"
                            defaultMessage="Brand equity"
                        />
                    </AgencyEyebrow>
                    {equity.insufficientSampleSize ? (
                        <div style={{ textAlign: 'center', padding: '32px 0' }}>
                            <div style={{ fontSize: 22, fontWeight: 800, color: THEME.textMuted }}>
                                <FormattedMessage
                                    id="agency.portfolio.collectingData"
                                    defaultMessage="Collecting data"
                                />
                            </div>
                            <p style={{ color: THEME.textSecondary, fontSize: 13.5 }}>
                                <FormattedMessage
                                    id="agency.portfolio.collectingDetail"
                                    defaultMessage="{n} of 10 responses needed"
                                    values={{ n: equity.totalResponses }}
                                />
                            </p>
                        </div>
                    ) : (
                        <>
                            <EquityArcGauge value={score ?? 0}>
                                <span
                                    style={{
                                        fontSize: 44,
                                        fontWeight: 800,
                                        letterSpacing: '-0.02em',
                                        lineHeight: 1,
                                        color: colourForScore('Brand Equity', score),
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {formatBipolarScore(score)}
                                </span>
                                <BandTag
                                    classification={equity.classificationLabel ?? null}
                                    band={bandFromClassification(
                                        equity.classificationLabel ?? null,
                                    )}
                                />
                            </EquityArcGauge>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: 14,
                                    marginTop: 18,
                                    alignItems: 'center',
                                    fontSize: 13,
                                    color: THEME.textSecondary,
                                }}
                            >
                                {equity.delta !== null && (
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 5,
                                        }}
                                    >
                                        <DeltaChip delta={equity.delta} />
                                        <FormattedMessage
                                            id="agency.scorecard.deltaLabel"
                                            defaultMessage="vs previous period"
                                        />
                                    </span>
                                )}
                                <span>
                                    <FormattedMessage
                                        id="agency.portfolio.responsesMeta"
                                        defaultMessage="{n} responses"
                                        values={{ n: equity.totalResponses }}
                                    />
                                </span>
                            </div>
                        </>
                    )}
                </Card>

                <Card>
                    <AgencyEyebrow>
                        <FormattedMessage
                            id="agency.scorecard.benchmarkTitle"
                            defaultMessage="Segment benchmark"
                        />
                    </AgencyEyebrow>
                    {benchmark?.available && benchmark.distribution ? (
                        <>
                            <div
                                style={{
                                    fontSize: 22,
                                    fontWeight: 800,
                                    letterSpacing: '-0.01em',
                                    marginBottom: 4,
                                }}
                            >
                                {positionLabel && (
                                    <FormattedMessage
                                        id={positionLabel.id}
                                        defaultMessage={positionLabel.def}
                                    />
                                )}
                            </div>
                            <p
                                style={{
                                    color: THEME.textSecondary,
                                    fontSize: 13.5,
                                    margin: '0 0 22px',
                                }}
                            >
                                <FormattedMessage
                                    id="agency.scorecard.benchmarkDetail"
                                    defaultMessage="{vs} vs the segment average across {n} brands in {bucket}."
                                    values={{
                                        vs: `${(benchmark.vsMean ?? 0) >= 0 ? '+' : ''}${(
                                            benchmark.vsMean ?? 0
                                        ).toFixed(1)}`,
                                        n: benchmark.distribution.sampleSize,
                                        bucket:
                                            [bucket.businessTypeName, bucket.productTypeName]
                                                .filter(Boolean)
                                                .join(' × ') || '—',
                                    }}
                                />
                            </p>
                            {/* Meter: client pin vs segment mean */}
                            <div style={{ position: 'relative', height: 34, marginBottom: 6 }}>
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 13,
                                        left: 0,
                                        right: 0,
                                        height: 10,
                                        borderRadius: 10,
                                        background: METER_GRADIENT,
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)',
                                    }}
                                />
                                <div
                                    title={intl.formatMessage(
                                        {
                                            id: 'agency.scorecard.meanMarker',
                                            defaultMessage: 'Segment average: {v}',
                                        },
                                        {
                                            v: formatBipolarScore(benchmark.distribution.mean),
                                        },
                                    )}
                                    style={{
                                        position: 'absolute',
                                        top: 7,
                                        left: `${gaugePosition(benchmark.distribution.mean)}%`,
                                        transform: 'translateX(-50%)',
                                        width: 3,
                                        height: 22,
                                        background: THEME.textBase,
                                        borderRadius: 2,
                                    }}
                                />
                                {score !== null && (
                                    <div
                                        title={`${client.clientOrganisationName}: ${formatBipolarScore(score)}`}
                                        style={{
                                            position: 'absolute',
                                            top: 4,
                                            left: `${gaugePosition(score)}%`,
                                            transform: 'translateX(-50%)',
                                            width: 16,
                                            height: 16,
                                            borderRadius: '50%',
                                            background: '#0F172A',
                                            border: '3px solid #fff',
                                            boxShadow: '0 2px 6px rgba(15,23,42,0.35)',
                                        }}
                                    />
                                )}
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    fontSize: 11.5,
                                    color: THEME.textMuted,
                                    marginBottom: 22,
                                }}
                            >
                                <span>−100</span>
                                <span>0</span>
                                <span>+100</span>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: 22,
                                    fontSize: 12.5,
                                    color: THEME.textSecondary,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <span>
                                    <FormattedMessage
                                        id="agency.scorecard.p25"
                                        defaultMessage="25th pct"
                                    />
                                    {': '}
                                    <strong>
                                        {formatBipolarScore(benchmark.distribution.percentile25)}
                                    </strong>
                                </span>
                                <span>
                                    <FormattedMessage
                                        id="agency.scorecard.median"
                                        defaultMessage="Median"
                                    />
                                    {': '}
                                    <strong>
                                        {formatBipolarScore(benchmark.distribution.percentile50)}
                                    </strong>
                                </span>
                                <span>
                                    <FormattedMessage
                                        id="agency.scorecard.p75"
                                        defaultMessage="75th pct"
                                    />
                                    {': '}
                                    <strong>
                                        {formatBipolarScore(benchmark.distribution.percentile75)}
                                    </strong>
                                </span>
                            </div>
                        </>
                    ) : (
                        <p style={{ color: THEME.textSecondary, fontSize: 13.5 }}>
                            {benchmark?.reason || (
                                <FormattedMessage
                                    id="agency.scorecard.benchmarkUnavailable"
                                    defaultMessage="Benchmark data is not yet available for this segment."
                                />
                            )}
                        </p>
                    )}
                </Card>
            </div>

            {/* Building blocks */}
            <AgencyEyebrow>
                <FormattedMessage
                    id="agency.scorecard.elementsTitle"
                    defaultMessage="Building blocks"
                />
            </AgencyEyebrow>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: 12,
                    marginBottom: 28,
                }}
            >
                {equity.latentElementScores.map((el) => {
                    const colour = el.locked
                        ? '#CBD5E1'
                        : colourForScore(el.element, el.score);
                    return (
                        <div
                            key={el.element}
                            style={{
                                background: '#fff',
                                border: `1px solid ${THEME.border}`,
                                borderTop: `3px solid ${colour}`,
                                borderRadius: 13,
                                padding: '13px 14px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6,
                            }}
                        >
                            {el.locked ? (
                                <span style={{ fontSize: 20, color: '#9ca3af', lineHeight: 1 }}>
                                    <LockOutlined />
                                </span>
                            ) : (
                                <span
                                    style={{
                                        fontSize: 22,
                                        fontWeight: 800,
                                        color: colour,
                                        letterSpacing: '-0.03em',
                                        lineHeight: 1,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {formatBipolarScore(el.score)}
                                </span>
                            )}
                            <span
                                style={{
                                    fontSize: 12.5,
                                    fontWeight: 600,
                                    color: THEME.textSecondary,
                                    lineHeight: 1.3,
                                }}
                            >
                                {el.element}
                            </span>
                            {!el.locked && el.classificationLabel && (
                                <span>
                                    <BandTag
                                        classification={el.classificationLabel}
                                        band={bandFromClassification(el.classificationLabel)}
                                    />
                                </span>
                            )}
                            {el.locked && (
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: THEME.primary,
                                    }}
                                >
                                    <FormattedMessage
                                        id="agency.scorecard.lockedElement"
                                        defaultMessage="Not in client's plan"
                                    />
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Trend */}
            <AgencyEyebrow>
                <FormattedMessage id="agency.scorecard.trendTitle" defaultMessage="Trend" />
            </AgencyEyebrow>
            <Card style={{ marginBottom: 28 }}>
                {trendPoints.filter((p) => p.score !== null).length >= 2 ? (
                    <>
                        <ResponsiveContainer width="100%" height={280}>
                            <LineChart
                                data={trendPoints}
                                margin={{ top: 10, right: 16, bottom: 4, left: 0 }}
                            >
                                <CartesianGrid
                                    stroke={THEME.borderSubtle}
                                    vertical={false}
                                />
                                <XAxis
                                    dataKey="month"
                                    tick={{ fontSize: 11.5, fill: THEME.textMuted }}
                                    tickLine={false}
                                    axisLine={{ stroke: THEME.border }}
                                    tickFormatter={(v: string) =>
                                        new Date(v).toLocaleDateString('en-GB', {
                                            day: 'numeric',
                                            month: 'short',
                                        })
                                    }
                                />
                                <YAxis
                                    tick={{ fontSize: 11.5, fill: THEME.textMuted }}
                                    tickLine={false}
                                    axisLine={false}
                                    width={40}
                                    domain={['auto', 'auto']}
                                />
                                <ReferenceLine y={0} stroke={THEME.border} />
                                <ChartTooltip
                                    contentStyle={{
                                        borderRadius: 8,
                                        border: `1px solid ${THEME.border}`,
                                        boxShadow: THEME.cardShadow,
                                        fontSize: 12.5,
                                    }}
                                    formatter={(value: any, _name: any, item: any) => {
                                        const p = item?.payload as
                                            | (typeof trendPoints)[number]
                                            | undefined;
                                        const suffix = p?.lowConfidence
                                            ? ` · ${intl.formatMessage({
                                                  id: 'agency.scorecard.lowConfidence',
                                                  defaultMessage: 'low confidence',
                                              })}`
                                            : '';
                                        return [
                                            `${value}% (${p?.responses ?? 0} responses${suffix})`,
                                            intl.formatMessage({
                                                id: 'agency.spark.tooltipScore',
                                                defaultMessage: 'Brand equity',
                                            }),
                                        ];
                                    }}
                                    labelFormatter={(v: string) =>
                                        new Date(v).toLocaleDateString('en-GB', {
                                            day: 'numeric',
                                            month: 'short',
                                            year: 'numeric',
                                        })
                                    }
                                />
                                <Line
                                    type="monotone"
                                    dataKey="score"
                                    stroke={LINE_COLOUR}
                                    strokeWidth={2.5}
                                    connectNulls={false}
                                    isAnimationActive={false}
                                    dot={(props: any) => {
                                        const p = props.payload as (typeof trendPoints)[number];
                                        if (p.score === null) return <g key={props.index} />;
                                        return (
                                            <circle
                                                key={props.index}
                                                cx={props.cx}
                                                cy={props.cy}
                                                r={4}
                                                fill={LINE_COLOUR}
                                                stroke={
                                                    p.lowConfidence
                                                        ? LOW_CONFIDENCE_COLOUR
                                                        : '#fff'
                                                }
                                                strokeWidth={p.lowConfidence ? 3 : 2}
                                            />
                                        );
                                    }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                        <div
                            style={{
                                display: 'flex',
                                gap: 18,
                                marginTop: 10,
                                fontSize: 12,
                                color: THEME.textMuted,
                                flexWrap: 'wrap',
                            }}
                        >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span
                                    style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        background: LINE_COLOUR,
                                        border: `3px solid ${LOW_CONFIDENCE_COLOUR}`,
                                        boxSizing: 'content-box',
                                        display: 'inline-block',
                                    }}
                                />
                                <FormattedMessage
                                    id="agency.scorecard.lowConfidenceLegend"
                                    defaultMessage="Amber ring = fewer responses than usual in that window"
                                />
                            </span>
                            <span>
                                <FormattedMessage
                                    id="agency.scorecard.hiddenBuckets"
                                    defaultMessage="Windows with under {n} responses are hidden"
                                    values={{ n: trend?.minResponseThreshold ?? 10 }}
                                />
                            </span>
                        </div>
                    </>
                ) : (
                    <p style={{ color: THEME.textSecondary, fontSize: 13.5, margin: 0 }}>
                        <FormattedMessage
                            id="agency.scorecard.noTrend"
                            defaultMessage="The trend chart appears once there is enough response history."
                        />
                    </p>
                )}
            </Card>

            {/* Strengths / focus + activity */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 16,
                }}
            >
                <Card>
                    <AgencyEyebrow>
                        <FormattedMessage
                            id="agency.scorecard.strengths"
                            defaultMessage="Strengths"
                        />
                    </AgencyEyebrow>
                    <CategoryList items={categories.strengths} />
                </Card>
                <Card>
                    <AgencyEyebrow>
                        <FormattedMessage
                            id="agency.scorecard.focus"
                            defaultMessage="Focus areas"
                        />
                    </AgencyEyebrow>
                    <CategoryList items={categories.focus} />
                </Card>
                <Card>
                    <AgencyEyebrow>
                        <FormattedMessage
                            id="agency.scorecard.activity"
                            defaultMessage="Recent activity"
                        />
                    </AgencyEyebrow>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {data.recentActivity.length === 0 && (
                            <span style={{ fontSize: 13, color: THEME.textMuted }}>
                                <FormattedMessage
                                    id="agency.scorecard.noActivity"
                                    defaultMessage="No recent activity"
                                />
                            </span>
                        )}
                        {data.recentActivity.slice(0, 8).map((e) => (
                            <div
                                key={e.id}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 10,
                                    fontSize: 12.5,
                                }}
                            >
                                <span
                                    style={{
                                        color: THEME.textSecondary,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {formatEventType(e.eventType)}
                                </span>
                                <span style={{ color: THEME.textMuted, flexShrink: 0 }}>
                                    {timeAgo(e.createdAt)}
                                </span>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
};

const CategoryList: React.FunctionComponent<{
    items: Array<{
        category: string;
        meanScore: number | null;
        classificationLabel?: string;
    }>;
}> = ({ items }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.length === 0 && (
            <span style={{ fontSize: 13, color: '#94A3B8' }}>
                <FormattedMessage id="agency.scorecard.noCategories" defaultMessage="No data yet" />
            </span>
        )}
        {items.map((c) => (
            <div
                key={c.category}
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 10,
                }}
            >
                <span
                    style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#334155',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {c.category}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span
                        style={{
                            fontSize: 14,
                            fontWeight: 800,
                            color: colourForScore(c.category, c.meanScore),
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {formatBipolarScore(c.meanScore)}
                    </span>
                    <BandTag
                        classification={c.classificationLabel ?? null}
                        band={bandFromClassification(c.classificationLabel ?? null)}
                    />
                </span>
            </div>
        ))}
    </div>
);
